import { registerPlugin } from "@capacitor/core";

/**
 * What the iOS shell reports about the display (HiiveDisplayPlugin.swift).
 *
 * `fold` is iPhone Duo's crease on the inner display, in web-view viewport
 * pixels, already widened by Apple's keep-clear margins. It is null on every
 * other device, on the outer display, while the phone is closed, and on a
 * binary or OS without the iOS 27.1 reserved-region API.
 */
export type NativeDisplayFold = {
  x: number;
  y: number;
  width: number;
  height: number;
  /** `vertical`: the crease runs top to bottom (the inner display held wide). */
  orientation: "vertical" | "horizontal";
  /** False while the phone lies open flat: the crease is there but inactive. */
  isActive: boolean;
};

/**
 * The column iPhone Duo stacks its status bar (and, closed, its camera) in, on
 * one side edge — where Apple puts an app's tab bar, back button and toolbar
 * buttons. Null when the system bars are on top (every other iPhone, and Duo's
 * inner display held upright). `width` is the side inset; `top` is where app
 * controls may start, below the status stack.
 */
export type NativeSystemColumn = {
  edge: "left" | "right";
  width: number;
  top: number;
};

/**
 * The status stack when it sits in a top corner instead of across the top
 * (iPhone Duo's inner display held upright). The top safe-area inset still
 * spans the whole width; this says how much of it is really taken, so a
 * header can sit beside the corner instead of below an empty band.
 */
export type NativeStatusCorner = {
  edge: "left" | "right";
  width: number;
  height: number;
};

export type NativeDisplayState = {
  supported: boolean;
  fold: NativeDisplayFold | null;
  /** `book` / `tabletop` while half folded; `flat` open; `closed` shut. */
  posture: "flat" | "book" | "tabletop" | "closed" | null;
  activeDisplay: "inner" | "outer" | null;
  hingeAngle: number | null;
  horizontalSizeClass: "compact" | "regular" | "unspecified";
  verticalSizeClass: "compact" | "regular" | "unspecified";
  /** Absent from shells built before it was reported. */
  systemColumn?: NativeSystemColumn | null;
  statusCorner?: NativeStatusCorner | null;
};

type HiiveDisplayPlugin = {
  getState(): Promise<NativeDisplayState>;
  addListener(
    eventName: "change",
    listener: (state: NativeDisplayState) => void,
  ): Promise<{ remove: () => Promise<void> }>;
};

export const HiiveDisplay = registerPlugin<HiiveDisplayPlugin>("HiiveDisplay");

const FOLD_CLASSES = ["fold-vertical", "fold-horizontal"] as const;
const POSTURE_CLASSES = [
  "posture-flat",
  "posture-book",
  "posture-tabletop",
] as const;
const DISPLAY_CLASSES = ["display-inner", "display-outer"] as const;
const SIDE_NAV_CLASSES = [
  "side-nav",
  "side-nav-left",
  "side-nav-right",
] as const;
const STATUS_CORNER_CLASSES = [
  "status-corner",
  "status-corner-left",
  "status-corner-right",
] as const;
const FOLD_VARIABLES = [
  "--fold-left",
  "--fold-top",
  "--fold-width",
  "--fold-height",
  "--side-nav-width",
  "--side-nav-top",
  "--status-corner-width",
  "--status-corner-height",
] as const;

/**
 * Mirrors a display state onto `<html>`: the classes and variables the
 * fold-aware layouts in globals.css read. `null` clears everything, which is
 * also the state of a browser or a phone without a fold — every rule keyed on
 * these is an enhancement over the plain layout.
 */
export function applyDisplayState(
  root: HTMLElement,
  state: NativeDisplayState | null,
) {
  root.classList.remove(
    ...FOLD_CLASSES,
    ...POSTURE_CLASSES,
    ...DISPLAY_CLASSES,
    ...SIDE_NAV_CLASSES,
    ...STATUS_CORNER_CLASSES,
  );
  for (const variable of FOLD_VARIABLES) root.style.removeProperty(variable);
  if (!state) return;

  // `side-nav` (globals.css) moves the tab rail, the header's buttons and a
  // detail page's back/share into the system column, as iOS does natively.
  const column = state.systemColumn;
  if (column && column.width > 0) {
    root.classList.add("side-nav", `side-nav-${column.edge}`);
    root.style.setProperty("--side-nav-width", `${column.width}px`);
    root.style.setProperty("--side-nav-top", `${column.top}px`);
  }

  // `status-corner` (globals.css) lets headers rise into the top band
  // beside the corner instead of under the full-width inset.
  const corner = state.statusCorner;
  if (!column && corner && corner.width > 0 && corner.height > 0) {
    root.classList.add("status-corner", `status-corner-${corner.edge}`);
    root.style.setProperty("--status-corner-width", `${corner.width}px`);
    root.style.setProperty("--status-corner-height", `${corner.height}px`);
  }

  if (state.activeDisplay) root.classList.add(`display-${state.activeDisplay}`);
  if (state.posture && state.posture !== "closed") {
    root.classList.add(`posture-${state.posture}`);
  }

  const { fold } = state;
  if (!fold || fold.width < 0 || fold.height < 0) return;
  root.classList.add(`fold-${fold.orientation}`);
  root.style.setProperty("--fold-left", `${fold.x}px`);
  root.style.setProperty("--fold-top", `${fold.y}px`);
  root.style.setProperty("--fold-width", `${fold.width}px`);
  root.style.setProperty("--fold-height", `${fold.height}px`);
}
